import { GitMerge } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { SyncCenterPanel } from "./SyncCenterPanel";
import { useSyncStatus } from "../../hooks/useSyncEngine";

const STATE_STYLES = {
  live: { dot: "bg-emerald-500", label: "Live" },
  syncing: { dot: "bg-amber-500 animate-pulse", label: "Syncing" },
  bootstrapping: { dot: "bg-amber-500 animate-pulse", label: "Downloading" },
  offline: { dot: "bg-red-500", label: "Offline" },
  error: { dot: "bg-red-500 animate-pulse", label: "Sync error" },
  idle: { dot: "bg-zinc-400", label: "Paused" },
};

const formatClock = (isoValue) => (isoValue ? new Date(isoValue).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "");

export const SyncStatusIndicator = ({ user }) => {
  const status = useSyncStatus();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const style = STATE_STYLES[status.state] || STATE_STYLES.idle;
  const pendingLabel = status.pendingCount ? ` · ${status.pendingCount} pending` : "";
  const clock = formatClock(status.lastSyncAt);
  const showConflicts = user?.role === "Admin" && status.openConflicts > 0;

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const handleClick = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        className="inline-flex items-center gap-2 whitespace-nowrap rounded-2xl border border-zinc-200 bg-white px-3 py-3 text-xs font-semibold text-zinc-700 transition hover:border-zinc-900 hover:text-zinc-900"
        data-testid="sync-status-indicator"
        onClick={() => setOpen((current) => !current)}
        title="Open Sync Center"
        type="button"
      >
        <span className={`h-2.5 w-2.5 rounded-full ${style.dot}`} data-testid="sync-status-dot" />
        <span data-testid="sync-status-label">{style.label}{pendingLabel}</span>
        {clock && status.state === "live" ? <span className="hidden font-normal text-zinc-400 lg:inline" data-testid="sync-status-clock">{clock}</span> : null}
        {showConflicts ? <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-900" data-testid="sync-status-conflicts-badge" title="Open sync conflicts"><GitMerge className="h-3 w-3" />{status.openConflicts}</span> : null}
      </button>
      {open ? <SyncCenterPanel user={user} /> : null}
    </div>
  );
};
